'use client';

import { useTranslation } from '@/features/i18n';
import { Menu } from '@headlessui/react';
import {
  ArrowPathIcon,
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { getEnvVar } from '@vimond/utils/env';

type ThemeSwitchProps = {
  color?: string;
  menuposition?: 'over' | 'under';
};

export const ThemeSwitch = ({ color, menuposition }: ThemeSwitchProps) => {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme, setTheme, theme } = useTheme();
  const { t } = useTranslation();

  // useEffect only runs on the client, so now we can safely show the UI
  useEffect(() => {
    setMounted(true);
  }, []);

  if (getEnvVar('NEXT_PUBLIC_THEME_SWITCHER_ENABLED') === 'false') return;

  if (!mounted) {
    return (
      <ArrowPathIcon
        className="h-6 w-6 animate-spin opacity-30"
        color="white"
      />
    );
  }

  return (
    <Menu as="div" className="relative opacity-70 hover:opacity-100">
      <Menu.Button className="relative flex rounded-md text-sm">
        <span className="absolute -inset-1.5" />
        <span className="sr-only">{t('open-theme-menu')}</span>
        <div className="group my-0">
          <div>
            {resolvedTheme === 'dark' && (
              <MoonIcon
                className="h-6 w-6"
                color={color ?? 'text-text-primary'}
              />
            )}
            {resolvedTheme === 'light' && (
              <SunIcon
                className="h-6 w-6"
                color={color ?? 'text-text-primary'}
              />
            )}
          </div>
        </div>
      </Menu.Button>
      <Menu.Items
        className={`bg-background-primary absolute right-0 z-10 mt-2 origin-top-right rounded-md py-1 shadow-lg focus:outline-none ${
          menuposition === 'over' ? '-translate-y-full translate-x-full' : ''
        }`}
      >
        <Menu.Item>
          <button
            className={`hover:bg-background-secondary flex w-full gap-1 px-4 py-2 text-left text-sm ${
              theme === 'light' ? 'font-bold' : 'opacity-70'
            }`}
            onClick={() => setTheme('light')}
          >
            <SunIcon className="mr-2 h-5 w-5" />
            {t('theme.light')}
          </button>
        </Menu.Item>
        <Menu.Item>
          <button
            className={`hover:bg-background-secondary flex w-full gap-1 px-4 py-2 text-left text-sm ${
              theme === 'dark' ? 'font-bold' : 'opacity-70'
            }`}
            onClick={() => setTheme('dark')}
          >
            <MoonIcon className="mr-2 h-5 w-5" />
            {t('theme.dark')}
          </button>
        </Menu.Item>
        <Menu.Item>
          <button
            className={`hover:bg-background-secondary flex w-full gap-1 px-4 py-2 text-left text-sm ${
              theme === 'system' ? 'font-bold' : 'opacity-70'
            }`}
            onClick={() => setTheme('system')}
          >
            <ComputerDesktopIcon className="mr-2 h-5 w-5" />
            {t('theme.system')}
          </button>
        </Menu.Item>
      </Menu.Items>
    </Menu>
  );
};
